import React, { useState, useEffect, useCallback } from "react";
import { api, Document, IndexStats } from "../api";

export default function DocumentsPanel() {
  const [documents, setDocuments]       = useState<Document[]>([]);
  const [stats, setStats]               = useState<IndexStats | null>(null);
  const [uploading, setUploading]       = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const [loading, setLoading]           = useState(true);
  const [syncing, setSyncing]           = useState(false);
  const [syncMessage, setSyncMessage]   = useState<{ text: string; ok: boolean } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [docs, s] = await Promise.all([api.getDocuments(), api.getStats()]);
      setDocuments(docs);
      setStats(s);
    } catch {
      // backend may not be running yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMessage(null);
    try {
      const result = await api.uploadDocument(file);
      setUploadMessage({ text: result.message, ok: true });
      await refresh();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Upload failed. Please try again.";
      setUploadMessage({ text: detail, ok: false });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSharepointSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await api.syncSharepoint();
      setSyncMessage({
        text: `Synced ${result.synced} file${result.synced !== 1 ? "s" : ""}, ${result.total_chunks.toLocaleString()} chunks indexed. ${result.skipped > 0 ? `(${result.skipped} skipped, ` : "("}${result.errors} error${result.errors !== 1 ? "s" : ""})`,
        ok: result.errors === 0,
      });
      await refresh();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Sync failed. Check SharePoint configuration in .env.";
      setSyncMessage({ text: detail, ok: false });
    } finally {
      setSyncing(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024)    return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="documents-panel">

      {/* Stats row */}
      <div className="stats-bar">
        <div className="stat-item">
          <div className="stat-value">{stats ? stats.total_documents : "—"}</div>
          <div className="stat-label">Documents</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{stats ? stats.total_chunks.toLocaleString() : "—"}</div>
          <div className="stat-label">Indexed Chunks</div>
        </div>
        <div className="stat-item">
          <div className="stat-value" style={{ fontSize: "0.95rem" }}>
            {stats ? stats.index_name : "—"}
          </div>
          <div className="stat-label">Search Index</div>
        </div>
      </div>

      {/* Upload */}
      <div className="upload-section">
        <h2>Upload Company Document</h2>
        <p>Supported formats: PDF, Word (.docx), Markdown (.md) — extracted, chunked and vectorised automatically.</p>
        <label className={`upload-btn ${uploading ? "disabled" : ""}`}>
          <span>📤</span>
          {uploading ? "Processing…" : "Choose file to upload"}
          <input
            type="file"
            accept=".pdf,.docx,.md"
            onChange={handleUpload}
            disabled={uploading}
            hidden
          />
        </label>
        {uploadMessage && (
          <div className={`upload-msg ${uploadMessage.ok ? "ok" : "err"}`}>
            {uploadMessage.ok ? "✓ " : "✗ "}{uploadMessage.text}
          </div>
        )}
      </div>

      {/* SharePoint sync */}
      <div className="upload-section">
        <h2>Sync from SharePoint</h2>
        <p>Pull supported documents (PDF, Word, Markdown) from a configured SharePoint site into the search index. Requires <code>SHAREPOINT_*</code> variables set in <code>.env</code>.</p>
        <button
          className={`upload-btn ${syncing ? "disabled" : ""}`}
          onClick={handleSharepointSync}
          disabled={syncing}
          style={{ display: "inline-flex", cursor: syncing ? "not-allowed" : "pointer" }}
        >
          <span>☁️</span>
          {syncing ? "Syncing…" : "Sync from SharePoint"}
        </button>
        {syncMessage && (
          <div className={`upload-msg ${syncMessage.ok ? "ok" : "err"}`}>
            {syncMessage.ok ? "✓ " : "✗ "}{syncMessage.text}
          </div>
        )}
      </div>

      {/* Document list */}
      <div className="doc-list">
        <div className="doc-list-header">
          <h2>Indexed Documents</h2>
          <button
            onClick={refresh}
            style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 10px", fontSize: "0.8rem", cursor: "pointer", color: "#6b7280" }}
          >
            ↻ Refresh
          </button>
        </div>

        {loading ? (
          <p className="hint">Loading…</p>
        ) : documents.length === 0 ? (
          <p className="hint">No documents indexed yet. Upload a PDF above to get started.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Size</th>
                <th>Last Modified</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.name}>
                  <td>
                    <div className="file-cell">
                      <span className="file-cell-icon">
                        {doc.name.endsWith(".pdf")  ? "📄" :
                         doc.name.endsWith(".docx") ? "📝" :
                         doc.name.endsWith(".md")   ? "📋" : "📄"}
                      </span>
                      {doc.name}
                    </div>
                  </td>
                  <td>{formatSize(doc.size)}</td>
                  <td>
                    {doc.last_modified
                      ? new Date(doc.last_modified).toLocaleDateString("en-GB", {
                          day: "2-digit", month: "short", year: "numeric",
                        })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

    </div>
  );
}
